import React from 'react';
import { APITypes, MedicalDataTypes } from '../../../../../primary-record-types';
import { resourceTypeGuards } from '../../../../../utils/medicalDataUtils';
import { getIntl } from '../../../../../utils/intl';
import * as Card from '../../../../v2BaseComponents/Card';
import getDateString from '../../../../../utils/getDateString';
import { getCategoryLabel } from '../../../../../utils/resourceUtils/messages';
import { useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import LazyLoadedReferenceCounts from '../../../../shared/ResourceCard/LazyLoadedReferenceCounts';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';

const intl = getIntl('profile-medical-data-resource-details-history', {
    header: '{category} History',
});

export type HistoryListOwnProps = {
    medicalProfileId: string;
    resource: APITypes.medicalDataService.ResourceDetailsResponse<MedicalDataTypes.PrimaryRecordResource>;
};

export type HistoryListProps = HistoryListOwnProps;

const HistoryList: React.FC<HistoryListProps> = (props) => {
    const navigator = useProfilesTabNavigation();

    if (!props.resource.groupedResources?.length) return null;

    const getSourceText = (
        resource:
            | APITypes.medicalDataService.ResourceDetailsResponse<MedicalDataTypes.PrimaryRecordResource>
            | APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordResource>,
    ) => {
        if (resource.metadata.source === '1uphealth') {
            return (resource.metadata as MedicalDataTypes.OneUpHealthResourceMetadata).healthSystemName;
        } else if (resource.metadata.source === 'fasten') {
            return (resource.metadata as MedicalDataTypes.FastenResourceMetadata).institutionName;
        }

        return null;
    };

    const renderCard = (
        resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordResource>,
        primaryText: string,
        sourceText: string | null,
    ) => (
        <Card.Card
            flat
            key={resource.id}
            onPress={() => {
                navigator.pop();
                navigator.navigate('data-details', {
                    profileId: props.medicalProfileId,
                    category: resource.type,
                    resourceId: resource.id,
                });
            }}
        >
            <Card.CardRightContent
                rightContent={
                    <Stack vertical justify="flex-end" align="flex-end" style={{ alignSelf: 'stretch', minWidth: 1 }}>
                        <LazyLoadedReferenceCounts resource={resource} medicalProfileId={props.medicalProfileId} />
                    </Stack>
                }
            >
                <Stack horizontal space={4} align="baseline">
                    <Card.CardHeader>{primaryText}</Card.CardHeader>
                    {sourceText ? <Card.CardBody>{`- ${sourceText}`}</Card.CardBody> : null}
                </Stack>
            </Card.CardRightContent>
        </Card.Card>
    );

    return (
        <Stack vertical space={contentStackSpacing} fullWidth>
            <Text variant="h4">{intl('header', { category: getCategoryLabel(props.resource.resource.type) })}</Text>
            {props.resource.groupedResources
                .filter((_, index, arr) => {
                    if (!resourceTypeGuards.isVaccination(_)) return true;
                    const dateStr = getDateString(_.dateTime!, 'date-time');
                    return !arr.some(
                        (other, otherIndex) =>
                            otherIndex < index &&
                            resourceTypeGuards.isVaccination(other) &&
                            getDateString(other.dateTime!, 'date-time') === dateStr,
                    );
                })
                .map((_) => {
                    if (resourceTypeGuards.isMedication(_)) {
                        return renderCard(_, getDateString(_.startDateTime!, 'date'), getSourceText(_));
                    } else if (resourceTypeGuards.isAllergy(_)) {
                        return renderCard(_, getDateString(_.startDateTime!, 'date'), getSourceText(_));
                    } else if (resourceTypeGuards.isCondition(_)) {
                        return renderCard(_, getDateString(_.startDateTime!, 'date'), getSourceText(_));
                    } else if (resourceTypeGuards.isVisit(_)) {
                        return renderCard(_, getDateString(_.startDateTime!, 'date'), getSourceText(_));
                    } else if (resourceTypeGuards.isVaccination(_)) {
                        return renderCard(_, getDateString(_.dateTime!, 'date'), getSourceText(_));
                    } else if (resourceTypeGuards.isProcedure(_)) {
                        return renderCard(_, getDateString(_.dateTime!, 'date'), getSourceText(_));
                    }
                })}
        </Stack>
    );
};

export default React.memo(HistoryList);
